// PoliceVerificationReport.jsx - Complete with PDF Print Functionality
import React, { useState, useEffect } from 'react';
import {
  FaSearch,
  FaFilter,
  FaDownload,
  FaEnvelope,
  FaPrint,
  FaCalendarAlt,
  FaUsers,
  FaUserCheck,
  FaBuilding,
  FaChartLine,
  FaEye,
  FaPhone,
  FaEnvelope as FaMailIcon,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaFileDownload,
  FaHotel,
  FaIdCard,
  FaPassport,
  FaBed,
  FaSpinner,
  FaFileAlt,
  FaTimes,
  FaSync,
  FaArrowUp,
  FaArrowDown,
  FaUserFriends,
  FaBriefcase,
} from 'react-icons/fa';
import { MdVerified, MdPending, MdOutlineVerifiedUser } from 'react-icons/md';
import { BsCalendarCheck, BsFillPersonCheckFill } from 'react-icons/bs';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AQ_BASE_URL = process.env.REACT_APP_AQ_BASE_URL;
const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

axios.defaults.withCredentials = true;

const PoliceVerificationReport = () => {
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [propertyId, setPropertyId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({
    recipientEmail: '',
    subject: '',
    message: '',
    includeAttachment: true,
  });
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await axios.get(`${AQ_BASE_URL}/aq/api/currentuser`, {
          withCredentials: true,
          headers: { accept: 'application/json' },
        });

        if (response.data && response.data.property_id) {
          setPropertyId(response.data.property_id);
          setCurrentUser(response.data);
          setEmailData((prev) => ({
            ...prev,
            subject: `Police Verification Report - ${selectedMonth} - Pagoda Xecutive`,
            message: `Dear Police Department,\n\nPlease find attached the Police Verification Report for the month of ${selectedMonth}.\n\nThis report contains guest verification details including:\n- Guest personal information\n- Stay duration and room details\n- Aadhaar verification status\n- Face recognition status\n- Signature verification\n\nGenerated by: Pagoda Xecutive \n\nBest regards,\nHotel Management System`,
          }));
        } else {
          throw new Error('Property ID not found in user data');
        }
      } catch (err) {
        console.error('Error fetching current user:', err);
        setError('Failed to load user information. Please ensure you are logged in.');
        setLoading(false);
      }
    };

    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (propertyId) {
      fetchReportData();
      fetchStatsData();
    }
  }, [propertyId, selectedMonth]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BQ_BASE_URL}/bq/api/reports/police-verification`, {
        params: {
          month: selectedMonth,
          property_id: propertyId,
          skip: (currentPage - 1) * itemsPerPage,
          limit: itemsPerPage,
        },
        withCredentials: true,
        headers: { accept: 'application/json' },
      });

      if (response.data && response.data.success) {
        setReportData(response.data);
        setError(null);
      } else {
        throw new Error('Failed to fetch report data');
      }
    } catch (err) {
      console.error('Error fetching report:', err);
      setError(err.response?.data?.detail || 'Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatsData = async () => {
    try {
      const response = await axios.get(`${BQ_BASE_URL}/bq/api/reports/police-verification/stats`, {
        params: { property_id: propertyId },
        withCredentials: true,
        headers: { accept: 'application/json' },
      });

      if (response.data && response.data.success) {
        setStatsData(response.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (propertyId) {
        fetchReportData();
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, filterStatus, currentPage]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // FULL PRINT FUNCTION WITH PDF - Complete as original
  const handlePrint = () => {
    const guestsToPrint = filteredGuests;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Police Verification Report - ${selectedMonth}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: 'Segoe UI', Arial, sans-serif;
            margin: 0;
            padding: 20px;
            background: white;
          }
          .report-container {
            max-width: 1200px;
            margin: 0 auto;
            background: white;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 3px solid #0F1A3A;
          }
          .header h1 {
            margin: 0;
            color: #0F1A3A;
            font-size: 28px;
            text-transform: uppercase;
            letter-spacing: 2px;
          }
          .header .subtitle {
            color: #666;
            font-size: 14px;
            margin-top: 5px;
          }
          .hotel-info {
            margin-top: 10px;
            padding: 10px;
            background: #f8f9fa;
            border-radius: 5px;
            font-size: 12px;
          }
          .summary {
            margin-bottom: 30px;
            padding: 20px;
            background: linear-gradient(135deg, #0F1A3A 0%, #1B2F5C 100%);
            border-radius: 10px;
            color: white;
          }
          .summary h3 {
            margin: 0 0 15px 0;
            font-size: 18px;
            border-bottom: 2px solid rgba(255,255,255,0.3);
            padding-bottom: 10px;
          }
          .summary-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 15px;
          }
          .summary-item {
            background: rgba(255,255,255,0.1);
            padding: 12px;
            border-radius: 8px;
            text-align: center;
          }
          .summary-item strong {
            display: block;
            font-size: 12px;
            margin-bottom: 8px;
            opacity: 0.9;
          }
          .summary-item span {
            font-size: 24px;
            font-weight: bold;
          }
          .guest-card {
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            margin-bottom: 20px;
            page-break-inside: avoid;
            break-inside: avoid;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          }
          .guest-header {
            background: #f8f9fa;
            padding: 15px 20px;
            border-bottom: 2px solid #0F1A3A;
            border-radius: 12px 12px 0 0;
          }
          .guest-header h3 {
            margin: 0;
            color: #0F1A3A;
            font-size: 18px;
          }
          .guest-header .booking-ref {
            color: #666;
            font-size: 12px;
            margin-top: 5px;
          }
          .guest-content {
            display: flex;
            padding: 20px;
            gap: 20px;
            flex-wrap: wrap;
          }
          .guest-photo-section {
            flex: 0 0 120px;
            text-align: center;
          }
          .guest-photo {
            width: 100px;
            height: 100px;
            border-radius: 50%;
            object-fit: cover;
            border: 3px solid #0F1A3A;
            margin-bottom: 8px;
            background: #f3f4f6;
          }
          .photo-label {
            font-size: 10px;
            color: #666;
            margin-top: 5px;
          }
          .guest-details {
            flex: 2;
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
            min-width: 300px;
          }
          .detail-row {
            display: flex;
            justify-content: space-between;
            padding: 6px 0;
            border-bottom: 1px dashed #e5e7eb;
          }
          .detail-label {
            font-weight: 600;
            color: #4b5563;
            font-size: 12px;
          }
          .detail-value {
            color: #1f2937;
            font-size: 12px;
          }
          .signature-section {
            flex: 1;
            text-align: center;
            border-left: 2px solid #e5e7eb;
            padding-left: 20px;
            min-width: 200px;
          }
          .signature-box {
            border: 1px solid #d1d5db;
            border-radius: 8px;
            padding: 15px;
            background: #f9fafb;
            min-height: 100px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .signature-image {
            max-width: 200px;
            max-height: 60px;
            object-fit: contain;
          }
          .signature-label {
            font-size: 10px;
            color: #6b7280;
            margin-top: 8px;
          }
          .aadhaar-section {
            margin-top: 15px;
            padding: 15px 20px;
            background: #fef3c7;
            border-top: 1px solid #fde68a;
            border-radius: 0 0 12px 12px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            gap: 10px;
          }
          .aadhaar-info {
            display: flex;
            gap: 20px;
            align-items: center;
            flex-wrap: wrap;
          }
          .aadhaar-badge {
            background: #C9A84C;
            color: white;
            padding: 4px 12px;
            border-radius: 20px;
            font-size: 11px;
            font-weight: bold;
          }
          .aadhaar-number {
            font-family: monospace;
            font-size: 14px;
            letter-spacing: 1px;
          }
          .verification-status {
            display: inline-block;
            padding: 4px 12px;
            border-radius: 20px;
            font-size: 11px;
            font-weight: bold;
          }
          .status-verified {
            background: #10b981;
            color: white;
          }
          .status-pending {
            background: #f59e0b;
            color: white;
          }
          .footer {
            margin-top: 40px;
            text-align: center;
            padding: 20px;
            border-top: 2px solid #e5e7eb;
            font-size: 11px;
            color: #6b7280;
          }
          .signature-stamp {
            margin-top: 30px;
            display: flex;
            justify-content: space-between;
            padding: 20px;
            flex-wrap: wrap;
            gap: 20px;
          }
          .signature-line {
            text-align: center;
            width: 250px;
          }
          .signature-line .line {
            border-top: 1px solid #000;
            margin-top: 40px;
            padding-top: 8px;
          }
          .image-placeholder {
            width: 100px;
            height: 100px;
            border-radius: 50%;
            background: #e5e7eb;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto;
            font-size: 40px;
          }
          @media print {
            body {
              padding: 0;
              margin: 0;
            }
            .guest-card {
              break-inside: avoid;
              page-break-inside: avoid;
            }
            .no-break {
              break-inside: avoid;
              page-break-inside: avoid;
            }
            img {
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
            }
          }
        </style>
      </head>
      <body>
        <div class="report-container">
          <!-- Header -->
          <div class="header">
            <h1>🏨 POLICE VERIFICATION REPORT</h1>
            <div class="subtitle">Hotel Guest Registration & Verification Records</div>
            <div class="hotel-info">
              <strong>Property ID:</strong> ${propertyId} | 
              <strong>Report Month:</strong> ${selectedMonth} | 
              <strong>Generated:</strong> ${new Date().toLocaleString()} | 
              <strong>Generated By:</strong> ${currentUser?.first_name || ''} ${currentUser?.last_name || ''}
            </div>
          </div>

          <!-- Summary Statistics -->
          <div class="summary">
            <h3>📊 VERIFICATION SUMMARY</h3>
            <div class="summary-grid">
              <div class="summary-item">
                <strong>Total Guests</strong>
                <span>${summary.total_guests}</span>
              </div>
              <div class="summary-item">
                <strong>Indian Guests</strong>
                <span>${summary.total_indian_guests}</span>
              </div>
              <div class="summary-item">
                <strong>Foreign Guests</strong>
                <span>${summary.total_foreign_guests}</span>
              </div>
              <div class="summary-item">
                <strong>Aadhaar Verified</strong>
                <span>${summary.aadhaar_verified}</span>
              </div>
              <div class="summary-item">
                <strong>Verified</strong>
                <span>${summary.verified_guests}</span>
              </div>
              <div class="summary-item">
                <strong>Pending</strong>
                <span>${summary.pending_verification}</span>
              </div>
              <div class="summary-item">
                <strong>Avg Stay</strong>
                <span>${summary.avg_stay_days} days</span>
              </div>
              <div class="summary-item">
                <strong>Occupancy</strong>
                <span>${summary.occupancy_rate}%</span>
              </div>
            </div>
          </div>

          <!-- Guest Cards -->
          <h3 style="margin: 20px 0 15px 0; color: #1f2937;">👥 GUEST VERIFICATION DETAILS</h3>
          
          ${guestsToPrint
            .map(
              (guest, idx) => `
            <div class="guest-card">
              <div class="guest-header">
                <h3>${guest.firstname || ''} ${guest.lastname || ''}</h3>
                <div class="booking-ref">
                  Booking ID: ${guest.booking_id || 'N/A'} | Order ID: ${guest.order_id || 'N/A'} | 
                  Room: ${guest.room_number ? '#' + guest.room_number : 'N/A'} | 
                  Status: ${guest.booking_status || 'N/A'}
                </div>
              </div>
              <div class="guest-content">
                <div class="guest-photo-section">
                  ${
                    guest.profile_photo
                      ? `<img src="${guest.profile_photo}" class="guest-photo" alt="Guest Photo" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />
                     <div class="image-placeholder" style="display: none;">📷</div>`
                      : `<div class="image-placeholder">📷</div>`
                  }
                  <div class="photo-label">Guest Photo</div>
                </div>
                <div class="guest-details">
                  <div class="detail-row">
                    <span class="detail-label">Full Name:</span>
                    <span class="detail-value">${guest.firstname || ''} ${guest.lastname || ''}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Phone Number:</span>
                    <span class="detail-value">${guest.countrycode || ''} ${guest.phonenumber || 'N/A'}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Email ID:</span>
                    <span class="detail-value">${guest.emailid || 'N/A'}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Country:</span>
                    <span class="detail-value">${guest.country || 'N/A'}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Check-in Date:</span>
                    <span class="detail-value">${guest.check_in_date || 'N/A'}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Check-out Date:</span>
                    <span class="detail-value">${guest.check_out_date || 'N/A'}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Room Number:</span>
                    <span class="detail-value">${guest.room_number ? '#' + guest.room_number : 'N/A'}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Room Type:</span>
                    <span class="detail-value">${guest.room_type || 'N/A'}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Purpose of Visit:</span>
                    <span class="detail-value">${guest.purpose_of_visit || 'N/A'}/{guest.}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Number of Guests:</span>
                    <span class="detail-value">${guest.number_of_guests || '1'}</span>
                  </div>
                </div>
                <div class="signature-section">
                  <div class="signature-box">
                    ${
                      guest.signature_url
                        ? `<img src="${guest.signature_url}" class="signature-image" alt="Guest Signature" onerror="this.parentElement.innerHTML='<div style=\\'\\'color:#9ca3af;\\\\'>No Signature Available</div>'" />`
                        : `<div style="color: #9ca3af;">No Signature Available</div>`
                    }
                  </div>
                  <div class="signature-label">Guest Signature</div>
                  ${
                    guest.signature_saved_at
                      ? `<div class="signature-label">Signed on: ${new Date(guest.signature_saved_at).toLocaleDateString()}</div>`
                      : ''
                  }
                </div>
              </div>
              <div class="aadhaar-section">
                <div class="aadhaar-info">
                  <span class="aadhaar-badge">AADHAAR</span>
                  ${
                    guest.aadhaar_verified
                      ? `<span class="status-verified verification-status">✓ Verified</span>`
                      : `<span class="status-pending verification-status">⚠ Pending</span>`
                  }
                  ${
                    guest.aadhaar_hash
                      ? `<span class="aadhaar-number">ID: ${guest.aadhaar_hash.substring(0, 12)}...</span>`
                      : ''
                  }
                </div>
                <div>
                  ${
                    guest.verification_status === 'completed'
                      ? `<span class="status-verified verification-status">✓ Police Verification Completed</span>`
                      : `<span class="status-pending verification-status">⚠ Police Verification Pending</span>`
                  }
                </div>
              </div>
            </div>
          `
            )
            .join('')}
          
          <!-- Signature & Stamp Section -->
          <div class="signature-stamp">
            <div class="signature-line">
              <div class="line">Authorized Signatory</div>
              <div style="font-size: 10px; margin-top: 5px;">Hotel Manager</div>
            </div>
            <div class="signature-line">
              <div class="line">Police Station Stamp</div>
              <div style="font-size: 10px; margin-top: 5px;">(For Official Use)</div>
            </div>
            <div class="signature-line">
              <div class="line">Receiving Officer Signature</div>
              <div style="font-size: 10px; margin-top: 5px;">Police Department</div>
            </div>
          </div>

          <!-- Footer -->
          <div class="footer">
            <p>This is a computer-generated police verification report. All information is based on hotel records.</p>
            <p>For verification or additional information, please contact: ${currentUser?.email || 'hotel@example.com'} | Phone: +91-XXXXXXXXXX</p>
            <p>© ${new Date().getFullYear()} Hotel Management System - Police Verification Report v1.0</p>
          </div>
        </div>
        
        <script>
          // Force images to load before printing
          window.onload = function() {
            var images = document.getElementsByTagName('img');
            var imagesLoaded = 0;
            var totalImages = images.length;
            
            function imageLoaded() {
              imagesLoaded++;
              if (imagesLoaded === totalImages) {
                window.print();
              }
            }
            
            for (var i = 0; i < totalImages; i++) {
              if (images[i].complete) {
                imageLoaded();
              } else {
                images[i].addEventListener('load', imageLoaded);
                images[i].addEventListener('error', imageLoaded);
              }
            }
            
            // If no images, print immediately
            if (totalImages === 0) {
              window.print();
            }
          };
        </script>
      </body>
    </html>
  `);
    printWindow.document.close();
  };

  const handleSendEmail = async () => {
    if (!emailData.recipientEmail) {
      toast.error('Please enter recipient email address');
      return;
    }

    setSendingEmail(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      toast.success(`Report sent successfully to ${emailData.recipientEmail}!`);
      setShowEmailModal(false);
    } catch (err) {
      console.error('Error sending email:', err);
      toast.error('Failed to send email. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${BQ_BASE_URL}/bq/api/reports/police-verification/export`, {
        params: {
          month: selectedMonth,
          property_id: propertyId,
          verification_status: filterStatus !== 'all' ? filterStatus : undefined,
        },
        withCredentials: true,
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `police_verification_${selectedMonth}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report exported successfully!');
    } catch (err) {
      console.error('Error exporting report:', err);
      toast.error('Failed to export report. Please try again.');
    }
  };

  const getFilteredGuests = () => {
    if (!reportData?.guests) return [];
    let filtered = [...reportData.guests];

    if (searchTerm) {
      filtered = filtered.filter(
        (guest) =>
          guest.firstname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          guest.lastname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          guest.booking_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          guest.order_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          guest.emailid?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          guest.phonenumber?.toString().includes(searchTerm)
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter((guest) => guest.verification_status === filterStatus);
    }

    return filtered;
  };

  const filteredGuests = getFilteredGuests();

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 flex items-center gap-1">
            <FaCheckCircle className="w-3 h-3" /> Verified
          </span>
        );
      case 'pending':
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800 flex items-center gap-1">
            <FaExclamationTriangle className="w-3 h-3" /> Pending
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">Unknown</span>
        );
    }
  };

  const getAadhaarStatusBadge = (isVerified, country) => {
    const isIndian = country?.toLowerCase() === 'india' || country?.toLowerCase() === 'indian';
    if (!isIndian) {
      return (
        <span className="text-xs text-gray-500 flex items-center gap-1">
          <FaPassport className="w-3 h-3" /> Not Applicable
        </span>
      );
    }
    if (isVerified) {
      return (
        <span className="text-xs text-green-600 flex items-center gap-1">
          <FaCheckCircle className="w-3 h-3" /> Aadhaar Verified
        </span>
      );
    }
    return (
      <span className="text-xs text-red-600 flex items-center gap-1">
        <FaExclamationTriangle className="w-3 h-3" /> Aadhaar Pending
      </span>
    );
  };

  const summary = reportData?.summary || {
    total_guests: 0,
    monthly_arrivals: 0,
    pending_verification: 0,
    verified_guests: 0,
    aadhaar_verified: 0,
    total_indian_guests: 0,
    total_foreign_guests: 0,
    occupancy_rate: 0,
    avg_stay_days: 0,
    unique_rooms_occupied: 0,
  };

  if (loading && !reportData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="w-12 h-12 text-[#0F1A3A] animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading police verification report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FaExclamationTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-2">Error loading report</p>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={() => {
              setError(null);
              setLoading(true);
              fetchReportData();
            }}
            className="mt-4 px-4 py-2 bg-[#0F1A3A] text-white rounded hover:bg-[#1B2F5C] transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Guest Details Modal Component - Full version preserved
  const GuestDetailsModal = ({ guest, onClose }) => {
    const [showFullImage, setShowFullImage] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState('');

    if (!guest) return null;

    const openImageViewer = (imageUrl) => {
      setSelectedImageUrl(imageUrl);
      setShowFullImage(true);
    };

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    };

    const formatPhoneNumber = (countryCode, phoneNumber) => {
      if (!phoneNumber) return 'N/A';
      return `${countryCode || ''} ${phoneNumber}`;
    };

    return (
      <>
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
          <div className="bg-white rounded-xl border border-gray-200 w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-white p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    {guest.profile_photo ? (
                      <img
                        src={guest.profile_photo}
                        alt="Profile"
                        className="w-16 h-16 rounded-full border-2 border-gray-300 object-cover cursor-pointer"
                        onClick={() => openImageViewer(guest.profile_photo)}
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gray-100 border-2 border-gray-300 flex items-center justify-center">
                        <FaUsers className="text-xl text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {guest.firstname} {guest.lastname}
                    </h2>
                    <p className="text-gray-600 text-sm">
                      Reservation ID: {guest.booking_id} • Order ID: {guest.order_id}
                    </p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded border border-gray-300">
                        Guest ID: {guest.guest_id?.substring(0, 8)}...
                      </span>
                      {guest.verification_status === 'completed' && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded border border-green-300 flex items-center space-x-1">
                          <FaCheckCircle className="text-xs" />
                          <span>Verified</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 text-xl font-bold p-2 rounded-full hover:bg-gray-100 transition"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Content - Rest of the modal content preserved */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                {/* Left Column */}
                <div className="xl:col-span-2 space-y-6">
                  {/* Personal Information with Corporate/Leisure Details */}
                  <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <FaUserCheck className="text-gray-600" />
                      <span>Personal Information</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <div className="flex justify-between items-center py-2 border-b border-gray-200">
                          <span className="text-sm font-medium text-gray-600">Full Name</span>
                          <span className="text-sm font-semibold text-gray-900">
                            {guest.firstname} {guest.lastname}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-200">
                          <span className="text-sm font-medium text-gray-600">Email</span>
                          <span className="text-sm text-gray-900 flex items-center space-x-1">
                            <FaMailIcon className="text-gray-400" />
                            <span>{guest.emailid || 'N/A'}</span>
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-200">
                          <span className="text-sm font-medium text-gray-600">Country</span>
                          <span className="text-sm text-gray-900">{guest.country}</span>
                        </div>
                        {/* Guest Type - Corporate/Leisure */}
                        <div className="flex justify-between items-center py-2 border-b border-gray-200">
                          <span className="text-sm font-medium text-gray-600">Guest Type</span>
                          <span
                            className={`text-sm font-semibold px-3 py-1 rounded-full ${
                              guest.client_type?.toLowerCase() === 'corporate'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-purple-100 text-purple-800'
                            }`}
                          >
                            {guest.client_type || 'Leisure'}
                          </span>
                        </div>
                        {/* Corporate Fields - Only show if corporate */}
                        {guest.client_type?.toLowerCase() === 'corporate' && (
                          <>
                            <div className="flex justify-between items-center py-2 border-b border-gray-200">
                              <span className="text-sm font-medium text-gray-600">Company ID</span>
                              <span className="text-sm font-mono text-gray-900">
                                {guest.company_id || 'N/A'}
                              </span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-gray-200">
                              <span className="text-sm font-medium text-gray-600">
                                Company Name
                              </span>
                              <span className="text-sm font-semibold text-gray-900">
                                {guest.company_name || 'N/A'}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center py-2 border-b border-gray-200">
                          <span className="text-sm font-medium text-gray-600">Phone</span>
                          <span className="text-sm font-semibold text-gray-900 flex items-center space-x-1">
                            <FaPhone className="text-gray-400" />
                            <span>{formatPhoneNumber(guest.countrycode, guest.phonenumber)}</span>
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-200">
                          <span className="text-sm font-medium text-gray-600">
                            Purpose of Visit
                          </span>
                          <span className="text-sm text-gray-900">
                            {guest.purpose_of_visit || 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2">
                          <span className="text-sm font-medium text-gray-600">
                            Number of Guests
                          </span>
                          <span className="text-sm font-semibold text-gray-900">
                            {guest.number_of_guests || '1'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Stay Details */}
                  <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <FaHotel className="text-gray-600" />
                      <span>Stay Details</span>
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
                        <BsCalendarCheck className="w-5 h-5 text-blue-500 mx-auto mb-2" />
                        <p className="text-xs text-gray-500">Check-in</p>
                        <p className="text-sm font-semibold">{guest.check_in_date || 'N/A'}</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
                        <BsCalendarCheck className="w-5 h-5 text-red-500 mx-auto mb-2" />
                        <p className="text-xs text-gray-500">Check-out</p>
                        <p className="text-sm font-semibold">{guest.check_out_date || 'N/A'}</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
                        <FaBed className="w-5 h-5 text-purple-500 mx-auto mb-2" />
                        <p className="text-xs text-gray-500">Room Number</p>
                        <p className="text-sm font-semibold">#{guest.room_number || 'N/A'}</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
                        <FaBuilding className="w-5 h-5 text-green-500 mx-auto mb-2" />
                        <p className="text-xs text-gray-500">Room Type</p>
                        <p className="text-sm font-semibold">{guest.room_type || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Aadhaar Card Section */}
                  <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <FaIdCard className="text-gray-600" />
                      <span>Aadhaar Card</span>
                    </h3>
                    {guest.aadhar_image_url ? (
                      <div className="text-center">
                        <img
                          src={guest.aadhar_image_url}
                          alt="Aadhaar Card"
                          className="max-w-full h-auto max-h-64 rounded border border-gray-300 mx-auto cursor-pointer hover:shadow-lg transition"
                          onClick={() => openImageViewer(guest.aadhar_image_url)}
                        />
                        <p className="text-gray-600 text-sm mt-3 flex items-center justify-center space-x-2">
                          <FaCheckCircle className="text-green-600" />
                          <span>Aadhaar document available</span>
                        </p>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="w-32 h-32 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg mx-auto flex items-center justify-center">
                          <FaIdCard className="text-4xl text-gray-400" />
                        </div>
                        <p className="text-gray-600 text-sm mt-3">No Aadhaar card uploaded</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  {/* Verification Status */}
                  <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <MdOutlineVerifiedUser className="text-gray-600" />
                      <span>Verification Status</span>
                    </h3>
                    <div className="space-y-3">
                      <div
                        className={`flex items-center justify-between p-3 rounded-lg border ${guest.verification_status === 'completed' ? 'bg-white border-green-300' : 'bg-white border-gray-300'}`}
                      >
                        <div className="flex items-center space-x-3">
                          <div
                            className={`p-2 rounded-full ${guest.verification_status === 'completed' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                          >
                            {guest.verification_status === 'completed' ? (
                              <FaCheckCircle />
                            ) : (
                              <FaTimesCircle />
                            )}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">Police Verification</p>
                            <p className="text-xs text-gray-600">
                              {guest.verification_status === 'completed'
                                ? 'Verification completed'
                                : 'Verification pending'}
                            </p>
                          </div>
                        </div>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${guest.verification_status === 'completed' ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-yellow-100 text-yellow-800 border border-yellow-300'}`}
                        >
                          {guest.verification_status === 'completed' ? 'VERIFIED' : 'PENDING'}
                        </span>
                      </div>

                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-300">
                        <div className="flex items-center space-x-3">
                          <div
                            className={`p-2 rounded-full ${guest.aadhaar_verified ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                          >
                            <FaIdCard />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">Aadhaar Verification</p>
                            <p className="text-xs text-gray-600">
                              {guest.aadhaar_verified ? 'Verified' : 'Not verified'}
                            </p>
                          </div>
                        </div>
                        <FaCheckCircle
                          className={guest.aadhaar_verified ? 'text-green-600' : 'text-gray-300'}
                        />
                      </div>

                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-300">
                        <div className="flex items-center space-x-3">
                          <div
                            className={`p-2 rounded-full ${guest.has_face_embedding ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                          >
                            <BsFillPersonCheckFill />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">Face Recognition</p>
                            <p className="text-xs text-gray-600">
                              {guest.has_face_embedding ? 'Face registered' : 'No face data'}
                            </p>
                          </div>
                        </div>
                        <FaCheckCircle
                          className={guest.has_face_embedding ? 'text-green-600' : 'text-gray-300'}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Signature Section */}
                  <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <FaFileAlt className="text-gray-600" />
                      <span>Guest Signature</span>
                    </h3>
                    {guest.signature_url ? (
                      <div className="text-center">
                        <div className="bg-white p-4 rounded-lg border border-gray-300">
                          <img
                            src={guest.signature_url}
                            alt="Guest Signature"
                            className="max-w-full h-auto max-h-32 mx-auto cursor-pointer"
                            onClick={() => openImageViewer(guest.signature_url)}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = `
                                <div class="text-gray-500 py-4">
                                  <FaFileAlt class="text-sm mx-auto mb-2" />
                                  <p>Signature URL is invalid</p>
                                </div>
                              `;
                            }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-3 flex items-center justify-center space-x-1">
                          <FaCheckCircle className="text-green-600 w-3 h-3" />
                          <span>
                            Signature captured on{' '}
                            {guest.signature_saved_at
                              ? formatDate(guest.signature_saved_at)
                              : 'check-in'}
                          </span>
                        </p>
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <div className="w-full h-32 bg-white border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                          <FaFileAlt className="text-4xl text-gray-400" />
                        </div>
                        <p className="text-gray-600 text-sm mt-3">No signature available</p>
                      </div>
                    )}
                  </div>

                  {/* Face Match Status */}
                  <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <BsFillPersonCheckFill className="text-gray-600" />
                      <span>Face Recognition Status</span>
                    </h3>
                    <div className="text-center">
                      <div
                        className={`p-4 rounded-lg ${guest.face_matched ? 'bg-green-100 border border-green-300' : 'bg-yellow-100 border border-yellow-300'}`}
                      >
                        {guest.face_matched ? (
                          <>
                            <FaCheckCircle className="w-12 h-12 text-green-600 mx-auto mb-2" />
                            <p className="font-semibold text-green-800">
                              Face Matched Successfully
                            </p>
                          </>
                        ) : (
                          <>
                            <FaExclamationTriangle className="w-12 h-12 text-yellow-600 mx-auto mb-2" />
                            <p className="font-semibold text-yellow-800">Face Not Matched</p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-6 bg-gray-50">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  Report generated for police verification
                </div>
                <div className="flex space-x-3">
                  {/* <button
                    onClick={handlePrint}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition flex items-center space-x-2"
                  >
                    <FaPrint className="text-sm" />
                    <span>Print Details</span>
                  </button> */}
                  <button
                    onClick={onClose}
                    className="px-4 py-2 bg-[#0F1A3A] text-white rounded-lg hover:bg-[#1B2F5C] transition font-semibold"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Full Screen Image Viewer */}
        {showFullImage && (
          <div
            className="fixed inset-0 bg-black bg-opacity-90 z-[60] flex items-center justify-center p-4"
            onClick={() => setShowFullImage(false)}
          >
            <div className="relative max-w-5xl max-h-[90vh]">
              <button
                onClick={() => setShowFullImage(false)}
                className="absolute -top-12 right-0 text-white hover:text-gray-300 text-2xl"
              >
                ✕
              </button>
              <img
                src={selectedImageUrl}
                alt="Full View"
                className="max-w-full max-h-[85vh] object-contain rounded-lg"
              />
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-[#0F1A3A] to-[#1B2F5C] rounded-xl flex items-center justify-center shadow-lg">
            <MdOutlineVerifiedUser className="text-[#C9A84C] text-2xl" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[#0F1A3A] flex items-center gap-2">
              Police Verification Report
            </h1>
            <p className="text-gray-600 font-medium mt-1">
              Comprehensive guest verification records for regulatory compliance
            </p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-2">
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-[#0F1A3A] hover:shadow-lg transition">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Guests (Month)</p>
              <p className="text-2xl font-bold text-[#0F1A3A]">{summary.total_guests}</p>
            </div>
            <FaUsers className="w-10 h-10 text-[#0F1A3A]" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-[#C9A84C] hover:shadow-lg transition">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Monthly Arrivals</p>
              <p className="text-2xl font-bold text-[#0F1A3A]">{summary.monthly_arrivals}</p>
              <p className="text-xs text-gray-500 mt-1">{selectedMonth}</p>
            </div>
            <FaChartLine className="w-10 h-10 text-[#C9A84C]" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-yellow-500 hover:shadow-lg transition">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pending Verification</p>
              <p className="text-2xl font-bold text-yellow-600">{summary.pending_verification}</p>
              {summary.pending_verification > 0 && (
                <p className="text-xs text-red-600 mt-1">Action Required!</p>
              )}
            </div>
            <MdPending className="w-10 h-10 text-yellow-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-[#2A4580] hover:shadow-lg transition">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Foreign Guests</p>
              <p className="text-2xl font-bold text-[#0F1A3A]">{summary.total_foreign_guests}</p>
            </div>
            <FaBuilding className="w-10 h-10 text-[#2A4580]" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-[#0F1A3A] hover:shadow-lg transition">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Corporate Guests</p>
              <p className="text-2xl font-bold text-[#0F1A3A]">{summary.corporate_guests || 0}</p>
            </div>
            <FaBriefcase className="w-10 h-10 text-[#0F1A3A]" />
          </div>
        </div>

        {/* Leisure Guests Card */}
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-[#C9A84C] hover:shadow-lg transition">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Leisure Guests</p>
              <p className="text-2xl font-bold text-[#0F1A3A]">{summary.leisure_guests || 0}</p>
            </div>
            <FaUserFriends className="w-10 h-10 text-[#C9A84C]" />
          </div>
        </div>
      </div>

      {/* Monthly Stats Summary */}
      {statsData?.monthly_stats &&
        statsData.monthly_stats.some((stat) => stat.total_guests > 0) && (
          <div className="px-1 py-3 bg-gray-50">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2 text-[#0F1A3A]">
              <FaChartLine className="text-[#C9A84C]" /> Monthly Trend (Last 6 Months)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {statsData.monthly_stats.map((stat, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-lg p-3 shadow hover:shadow-lg transition"
                >
                  <div className="text-center mb-2">
                    <span className="font-semibold text-sm text-[#0F1A3A]">{stat.month}</span>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-[#0F1A3A]">{stat.total_guests}</div>
                    <div className="text-xs text-gray-500">Guests</div>
                  </div>
                  <div className="mt-2 space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span>Verified:</span>
                      <span className="text-green-600">{stat.verified_guests}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Pending:</span>
                      <span className="text-yellow-600">{stat.pending_guests}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      {/* Second Row Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-3 hover:shadow-lg transition">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600 flex items-center gap-1">
              <FaIdCard className="text-[#0F1A3A]" /> Aadhaar Verified
            </span>
            <span className="text-lg font-bold text-green-600">{summary.aadhaar_verified}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
            <div
              className="bg-[#0F1A3A] rounded-full h-2"
              style={{
                width: `${summary.total_indian_guests > 0 ? (summary.aadhaar_verified / summary.total_indian_guests) * 100 : 0}%`,
              }}
            ></div>
          </div>
          <p className="text-xs text-gray-500 mt-1">Indian Guests: {summary.total_indian_guests}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-3 hover:shadow-lg transition">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600 flex items-center gap-1">
              <FaClock className="text-[#0F1A3A]" /> Avg. Stay Duration
            </span>
            <span className="text-lg font-bold text-[#0F1A3A]">{summary.avg_stay_days} days</span>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-3 hover:shadow-lg transition">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600 flex items-center gap-1">
              <FaHotel className="text-[#0F1A3A]" /> Current Occupancy
            </span>
            <span className="text-lg font-bold text-[#0F1A3A]">{summary.occupancy_rate}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
            <div
              className="bg-[#C9A84C] rounded-full h-2"
              style={{ width: `${summary.occupancy_rate}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Unique Rooms: {summary.unique_rooms_occupied}
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="flex flex-wrap justify-between items-center gap-4">
          <div className="flex gap-3">
            {/* <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition text-[#0F1A3A]"
            >
              <FaPrint /> Print Report
            </button> */}
            <button
              onClick={() => setShowEmailModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition text-[#0F1A3A]"
            >
              <FaEnvelope /> Email Report
            </button>
            {/* <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition text-[#0F1A3A]"
            >
              <FaFileDownload /> Export CSV
            </button> */}
          </div>
          <div className="flex gap-3">
            <div className="relative">
              <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, booking ID, phone, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
          >
            <option value="all">All Status</option>
            <option value="completed">Verified</option>
            <option value="pending">Pending</option>
          </select>
        </div>
      </div>

      {/* Guests Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200" id="printable-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Guest Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Guest Type
                </th>
                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stay Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Room
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Verification
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Aadhaar Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredGuests.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    No guests found for the selected period
                  </td>
                </tr>
              ) : (
                filteredGuests.map((guest, index) => (
                  <tr key={guest.booking_id || index} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {guest.profile_photo ? (
                            <img
                              className="h-10 w-10 rounded-full object-cover"
                              src={guest.profile_photo}
                              alt=""
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                              <FaUsers className="w-5 h-5 text-gray-400" />
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {guest.firstname} {guest.lastname}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center gap-1">
                            <FaPhone className="w-3 h-3" /> {guest.countrycode || ''}
                            {guest.phonenumber || 'N/A'}
                          </div>
                          <div className="text-xs text-gray-400 flex items-center gap-1">
                            <FaMailIcon className="w-3 h-3" /> {guest.emailid || 'N/A'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {guest.client_type?.toLowerCase() === 'corporate' ? (
                        <div className="flex flex-col gap-1">
                          <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 border border-blue-200 flex items-center gap-1">
                            <FaBriefcase className="w-3 h-3" /> Corporate
                          </span>
                          {guest.company_name && (
                            <span className="text-xs text-gray-600 ml-1">{guest.company_name}</span>
                          )}
                        </div>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800 border border-purple-200 flex items-center gap-1">
                          <FaUserFriends className="w-3 h-3" /> Leisure
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-4">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center gap-1 white-space-nowrap">
                          <BsCalendarCheck className="w-3 h-3" /> In: {guest.check_in_date || 'N/A'}
                        </div>
                        <div className="flex items-center gap-1 mt-1 whitespace-nowrap">
                          <BsCalendarCheck className="w-3 h-3" /> Out:{' '}
                          {guest.check_out_date || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 font-semibold">
                        Room {guest.room_number || 'N/A'}
                      </div>
                      <div className="text-xs text-gray-500">{guest.room_type || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4">{getStatusBadge(guest.verification_status)}</td>
                    <td className="px-6 py-4">
                      {getAadhaarStatusBadge(guest.aadhaar_verified, guest.country)}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => {
                          setSelectedGuest(guest);
                          setShowDetailsModal(true);
                        }}
                        className="text-[#0F1A3A] hover:text-[#C9A84C] transition flex items-center gap-1"
                      >
                        <FaEye /> View Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center mt-6">
        <div className="text-sm text-gray-500">
          Showing {filteredGuests.length} of {reportData?.total_count || 0} guests
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-[#0F1A3A]"
          >
            Previous
          </button>
          <button className="px-3 py-1 bg-[#0F1A3A] text-white rounded hover:bg-[#1B2F5C]">
            {currentPage}
          </button>
          <button
            onClick={() => setCurrentPage((prev) => prev + 1)}
            disabled={filteredGuests.length < itemsPerPage}
            className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-[#0F1A3A]"
          >
            Next
          </button>
        </div>
      </div>

      {/* Guest Details Modal */}
      {showDetailsModal && (
        <GuestDetailsModal guest={selectedGuest} onClose={() => setShowDetailsModal(false)} />
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-[#0F1A3A]">Email Report</h2>
              <button
                onClick={() => setShowEmailModal(false)}
                className="text-gray-400 hover:text-gray-600 transition p-1 rounded-lg hover:bg-gray-100"
              >
                <FaTimes size={18} />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">Send the police verification report to:</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={emailData.recipientEmail}
                  onChange={(e) =>
                    setEmailData({
                      ...emailData,
                      recipientEmail: e.target.value,
                    })
                  }
                  placeholder="police@example.com"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] text-sm font-medium"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  value={emailData.subject}
                  onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] text-sm font-medium"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  rows={4}
                  value={emailData.message}
                  onChange={(e) => setEmailData({ ...emailData, message: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] text-sm font-medium"
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="includeAttachment"
                  checked={emailData.includeAttachment}
                  onChange={(e) =>
                    setEmailData({
                      ...emailData,
                      includeAttachment: e.target.checked,
                    })
                  }
                  className="w-4 h-4 text-[#C9A84C] rounded focus:ring-[#C9A84C]"
                />
                <label htmlFor="includeAttachment" className="text-sm text-gray-700">
                  Include CSV report as attachment
                </label>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowEmailModal(false)}
                className="flex-1 py-2.5 border border-gray-300 rounded-xl hover:bg-gray-50 transition text-sm font-medium text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail || !emailData.recipientEmail}
                className="flex-1 py-2.5 bg-[#0F1A3A] text-white rounded-xl hover:bg-[#1B2F5C] transition flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-50 shadow-sm"
              >
                {sendingEmail ? <FaSpinner className="animate-spin" /> : <FaEnvelope />}
                {sendingEmail ? 'Sending...' : 'Send Report'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @media print {
          .bg-gray-50 {
            background: white !important;
          }
          button,
          .shadow {
            box-shadow: none !important;
          }
          .fixed {
            display: none !important;
          }
        }
        * {
          font-family:
            -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
            sans-serif;
        }
      `}</style>
    </div>
  );
};

export default PoliceVerificationReport;

// // PoliceVerificationReport.jsx
// import React, { useState, useEffect } from "react";
// import {
//   FaSearch,
//   FaFilter,
//   FaDownload,
//   FaEnvelope,
//   FaPrint,
//   FaCalendarAlt,
//   FaUsers,
//   FaUserCheck,
//   FaBuilding,
//   FaChartLine,
//   FaEye,
//   FaPhone,
//   FaEnvelope as FaMailIcon,
//   FaClock,
//   FaCheckCircle,
//   FaTimesCircle,
//   FaExclamationTriangle,
//   FaFileDownload,
//   FaHotel,
//   FaIdCard,
//   FaPassport,
//   FaBed,
//   FaSpinner,
//   FaFileAlt,
//   FaTimes,
// } from "react-icons/fa";
// import { MdVerified, MdPending, MdOutlineVerifiedUser } from "react-icons/md";
// import { BsCalendarCheck, BsFillPersonCheckFill } from "react-icons/bs";
// import axios from "axios";

// const AQ_BASE_URL = process.env.REACT_APP_AQ_BASE_URL;
// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// // Configure axios to send cookies with requests
// axios.defaults.withCredentials = true;

// const PoliceVerificationReport = () => {
//   const [selectedMonth, setSelectedMonth] = useState(() => {
//     const now = new Date();
//     return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
//   });
//   const [searchTerm, setSearchTerm] = useState("");
//   const [filterStatus, setFilterStatus] = useState("all");
//   const [selectedGuest, setSelectedGuest] = useState(null);
//   const [showDetailsModal, setShowDetailsModal] = useState(false);

//   // State for API data
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [reportData, setReportData] = useState(null);
//   const [statsData, setStatsData] = useState(null);
//   const [propertyId, setPropertyId] = useState(null);
//   const [currentUser, setCurrentUser] = useState(null);

//   // State for pagination
//   const [currentPage, setCurrentPage] = useState(1);
//   const [itemsPerPage] = useState(10);

//   // State for email modal
//   const [showEmailModal, setShowEmailModal] = useState(false);
//   const [emailData, setEmailData] = useState({
//     recipientEmail: "",
//     subject: "",
//     message: "",
//     includeAttachment: true,
//   });
//   const [sendingEmail, setSendingEmail] = useState(false);

//   // Fetch current user to get property_id (using cookies)
//   useEffect(() => {
//     const fetchCurrentUser = async () => {
//       try {
//         const response = await axios.get(`${AQ_BASE_URL}/aq/api/currentuser`, {
//           withCredentials: true,
//           headers: {
//             accept: "application/json",
//           },
//         });

//         if (response.data && response.data.property_id) {
//           setPropertyId(response.data.property_id);
//           setCurrentUser(response.data);
//           setEmailData((prev) => ({
//             ...prev,
//             subject: `Police Verification Report - ${selectedMonth} - Pagoda Xecutive`,
//             message: `Dear Police Department,\n\nPlease find attached the Police Verification Report for the month of ${selectedMonth}.\n\nThis report contains guest verification details including:\n- Guest personal information\n- Stay duration and room details\n- Aadhaar verification status\n- Face recognition status\n- Signature verification\n\nGenerated by: Pagoda Xecutive \n\nBest regards,\nHotel Management System`,
//           }));
//         } else {
//           throw new Error("Property ID not found in user data");
//         }
//       } catch (err) {
//         console.error("Error fetching current user:", err);
//         setError(
//           "Failed to load user information. Please ensure you are logged in.",
//         );
//         setLoading(false);
//       }
//     };

//     fetchCurrentUser();
//   }, []);

//   // Fetch report data when propertyId or selectedMonth changes
//   useEffect(() => {
//     if (propertyId) {
//       fetchReportData();
//       fetchStatsData();
//     }
//   }, [propertyId, selectedMonth]);

//   const fetchReportData = async () => {
//     try {
//       setLoading(true);

//       const response = await axios.get(
//         `${BQ_BASE_URL}/bq/api/reports/police-verification`,
//         {
//           params: {
//             month: selectedMonth,
//             property_id: propertyId,
//             skip: (currentPage - 1) * itemsPerPage,
//             limit: itemsPerPage,
//           },
//           withCredentials: true,
//           headers: {
//             accept: "application/json",
//           },
//         },
//       );

//       if (response.data && response.data.success) {
//         setReportData(response.data);
//         setError(null);
//       } else {
//         throw new Error("Failed to fetch report data");
//       }
//     } catch (err) {
//       console.error("Error fetching report:", err);
//       setError(err.response?.data?.detail || "Failed to load report data");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const fetchStatsData = async () => {
//     try {
//       const response = await axios.get(
//         `${BQ_BASE_URL}/bq/api/reports/police-verification/stats`,
//         {
//           params: {
//             property_id: propertyId,
//           },
//           withCredentials: true,
//           headers: {
//             accept: "application/json",
//           },
//         },
//       );

//       if (response.data && response.data.success) {
//         setStatsData(response.data);
//       }
//     } catch (err) {
//       console.error("Error fetching stats:", err);
//     }
//   };

//   // Handle search with debounce
//   useEffect(() => {
//     const delayDebounceFn = setTimeout(() => {
//       if (propertyId) {
//         fetchReportData();
//       }
//     }, 500);

//     return () => clearTimeout(delayDebounceFn);
//   }, [searchTerm, filterStatus, currentPage]);

//   // Handle Print - Professional Police Verification Report with Images
//   const handlePrint = () => {
//     // Get all guest data for the print
//     const guestsToPrint = filteredGuests;

//     const printWindow = window.open("", "_blank");
//     printWindow.document.write(`
//     <!DOCTYPE html>
//     <html>
//       <head>
//         <title>Police Verification Report - ${selectedMonth}</title>
//         <style>
//           * {
//             margin: 0;
//             padding: 0;
//             box-sizing: border-box;
//           }
//           body {
//             font-family: 'Segoe UI', Arial, sans-serif;
//             margin: 0;
//             padding: 20px;
//             background: white;
//           }
//           .report-container {
//             max-width: 1200px;
//             margin: 0 auto;
//             background: white;
//           }
//           .header {
//             text-align: center;
//             margin-bottom: 30px;
//             padding-bottom: 20px;
//             border-bottom: 3px solid #1a56db;
//           }
//           .header h1 {
//             margin: 0;
//             color: #1a56db;
//             font-size: 28px;
//             text-transform: uppercase;
//             letter-spacing: 2px;
//           }
//           .header .subtitle {
//             color: #666;
//             font-size: 14px;
//             margin-top: 5px;
//           }
//           .hotel-info {
//             margin-top: 10px;
//             padding: 10px;
//             background: #f8f9fa;
//             border-radius: 5px;
//             font-size: 12px;
//           }
//           .summary {
//             margin-bottom: 30px;
//             padding: 20px;
//             background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
//             border-radius: 10px;
//             color: white;
//           }
//           .summary h3 {
//             margin: 0 0 15px 0;
//             font-size: 18px;
//             border-bottom: 2px solid rgba(255,255,255,0.3);
//             padding-bottom: 10px;
//           }
//           .summary-grid {
//             display: grid;
//             grid-template-columns: repeat(4, 1fr);
//             gap: 15px;
//           }
//           .summary-item {
//             background: rgba(255,255,255,0.1);
//             padding: 12px;
//             border-radius: 8px;
//             text-align: center;
//           }
//           .summary-item strong {
//             display: block;
//             font-size: 12px;
//             margin-bottom: 8px;
//             opacity: 0.9;
//           }
//           .summary-item span {
//             font-size: 24px;
//             font-weight: bold;
//           }
//           .guest-card {
//             background: white;
//             border: 1px solid #e5e7eb;
//             border-radius: 12px;
//             margin-bottom: 20px;
//             page-break-inside: avoid;
//             break-inside: avoid;
//             box-shadow: 0 1px 3px rgba(0,0,0,0.1);
//           }
//           .guest-header {
//             background: #f8f9fa;
//             padding: 15px 20px;
//             border-bottom: 2px solid #1a56db;
//             border-radius: 12px 12px 0 0;
//           }
//           .guest-header h3 {
//             margin: 0;
//             color: #1a56db;
//             font-size: 18px;
//           }
//           .guest-header .booking-ref {
//             color: #666;
//             font-size: 12px;
//             margin-top: 5px;
//           }
//           .guest-content {
//             display: flex;
//             padding: 20px;
//             gap: 20px;
//             flex-wrap: wrap;
//           }
//           .guest-photo-section {
//             flex: 0 0 120px;
//             text-align: center;
//           }
//           .guest-photo {
//             width: 100px;
//             height: 100px;
//             border-radius: 50%;
//             object-fit: cover;
//             border: 3px solid #1a56db;
//             margin-bottom: 8px;
//             background: #f3f4f6;
//           }
//           .photo-label {
//             font-size: 10px;
//             color: #666;
//             margin-top: 5px;
//           }
//           .guest-details {
//             flex: 2;
//             display: grid;
//             grid-template-columns: repeat(2, 1fr);
//             gap: 10px;
//             min-width: 300px;
//           }
//           .detail-row {
//             display: flex;
//             justify-content: space-between;
//             padding: 6px 0;
//             border-bottom: 1px dashed #e5e7eb;
//           }
//           .detail-label {
//             font-weight: 600;
//             color: #4b5563;
//             font-size: 12px;
//           }
//           .detail-value {
//             color: #1f2937;
//             font-size: 12px;
//           }
//           .signature-section {
//             flex: 1;
//             text-align: center;
//             border-left: 2px solid #e5e7eb;
//             padding-left: 20px;
//             min-width: 200px;
//           }
//           .signature-box {
//             border: 1px solid #d1d5db;
//             border-radius: 8px;
//             padding: 15px;
//             background: #f9fafb;
//             min-height: 100px;
//             display: flex;
//             align-items: center;
//             justify-content: center;
//           }
//           .signature-image {
//             max-width: 200px;
//             max-height: 60px;
//             object-fit: contain;
//           }
//           .signature-label {
//             font-size: 10px;
//             color: #6b7280;
//             margin-top: 8px;
//           }
//           .aadhaar-section {
//             margin-top: 15px;
//             padding: 15px 20px;
//             background: #fef3c7;
//             border-top: 1px solid #fde68a;
//             border-radius: 0 0 12px 12px;
//             display: flex;
//             justify-content: space-between;
//             align-items: center;
//             flex-wrap: wrap;
//             gap: 10px;
//           }
//           .aadhaar-info {
//             display: flex;
//             gap: 20px;
//             align-items: center;
//             flex-wrap: wrap;
//           }
//           .aadhaar-badge {
//             background: #f59e0b;
//             color: white;
//             padding: 4px 12px;
//             border-radius: 20px;
//             font-size: 11px;
//             font-weight: bold;
//           }
//           .aadhaar-number {
//             font-family: monospace;
//             font-size: 14px;
//             letter-spacing: 1px;
//           }
//           .verification-status {
//             display: inline-block;
//             padding: 4px 12px;
//             border-radius: 20px;
//             font-size: 11px;
//             font-weight: bold;
//           }
//           .status-verified {
//             background: #10b981;
//             color: white;
//           }
//           .status-pending {
//             background: #f59e0b;
//             color: white;
//           }
//           .footer {
//             margin-top: 40px;
//             text-align: center;
//             padding: 20px;
//             border-top: 2px solid #e5e7eb;
//             font-size: 11px;
//             color: #6b7280;
//           }
//           .signature-stamp {
//             margin-top: 30px;
//             display: flex;
//             justify-content: space-between;
//             padding: 20px;
//             flex-wrap: wrap;
//             gap: 20px;
//           }
//           .signature-line {
//             text-align: center;
//             width: 250px;
//           }
//           .signature-line .line {
//             border-top: 1px solid #000;
//             margin-top: 40px;
//             padding-top: 8px;
//           }
//           .image-placeholder {
//             width: 100px;
//             height: 100px;
//             border-radius: 50%;
//             background: #e5e7eb;
//             display: flex;
//             align-items: center;
//             justify-content: center;
//             margin: 0 auto;
//             font-size: 40px;
//           }
//           @media print {
//             body {
//               padding: 0;
//               margin: 0;
//             }
//             .guest-card {
//               break-inside: avoid;
//               page-break-inside: avoid;
//             }
//             .no-break {
//               break-inside: avoid;
//               page-break-inside: avoid;
//             }
//             img {
//               print-color-adjust: exact;
//               -webkit-print-color-adjust: exact;
//             }
//           }
//         </style>
//       </head>
//       <body>
//         <div class="report-container">
//           <!-- Header -->
//           <div class="header">
//             <h1>🏨 POLICE VERIFICATION REPORT</h1>
//             <div class="subtitle">Hotel Guest Registration & Verification Records</div>
//             <div class="hotel-info">
//               <strong>Property ID:</strong> ${propertyId} |
//               <strong>Report Month:</strong> ${selectedMonth} |
//               <strong>Generated:</strong> ${new Date().toLocaleString()} |
//               <strong>Generated By:</strong> ${currentUser?.first_name || ""} ${currentUser?.last_name || ""}
//             </div>
//           </div>

//           <!-- Summary Statistics -->
//           <div class="summary">
//             <h3>📊 VERIFICATION SUMMARY</h3>
//             <div class="summary-grid">
//               <div class="summary-item">
//                 <strong>Total Guests</strong>
//                 <span>${summary.total_guests}</span>
//               </div>
//               <div class="summary-item">
//                 <strong>Indian Guests</strong>
//                 <span>${summary.total_indian_guests}</span>
//               </div>
//               <div class="summary-item">
//                 <strong>Foreign Guests</strong>
//                 <span>${summary.total_foreign_guests}</span>
//               </div>
//               <div class="summary-item">
//                 <strong>Aadhaar Verified</strong>
//                 <span>${summary.aadhaar_verified}</span>
//               </div>
//               <div class="summary-item">
//                 <strong>Verified</strong>
//                 <span>${summary.verified_guests}</span>
//               </div>
//               <div class="summary-item">
//                 <strong>Pending</strong>
//                 <span>${summary.pending_verification}</span>
//               </div>
//               <div class="summary-item">
//                 <strong>Avg Stay</strong>
//                 <span>${summary.avg_stay_days} days</span>
//               </div>
//               <div class="summary-item">
//                 <strong>Occupancy</strong>
//                 <span>${summary.occupancy_rate}%</span>
//               </div>
//             </div>
//           </div>

//           <!-- Guest Cards -->
//           <h3 style="margin: 20px 0 15px 0; color: #1f2937;">👥 GUEST VERIFICATION DETAILS</h3>

//           ${guestsToPrint
//             .map(
//               (guest, idx) => `
//             <div class="guest-card">
//               <div class="guest-header">
//                 <h3>${guest.firstname || ""} ${guest.lastname || ""}</h3>
//                 <div class="booking-ref">
//                   Booking ID: ${guest.booking_id || "N/A"} | Order ID: ${guest.order_id || "N/A"} |
//                   Room: ${guest.room_number ? "#" + guest.room_number : "N/A"} |
//                   Status: ${guest.booking_status || "N/A"}
//                 </div>
//               </div>
//               <div class="guest-content">
//                 <div class="guest-photo-section">
//                   ${
//                     guest.profile_photo
//                       ? `<img src="${guest.profile_photo}" class="guest-photo" alt="Guest Photo" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />
//                      <div class="image-placeholder" style="display: none;">📷</div>`
//                       : `<div class="image-placeholder">📷</div>`
//                   }
//                   <div class="photo-label">Guest Photo</div>
//                 </div>
//                 <div class="guest-details">
//                   <div class="detail-row">
//                     <span class="detail-label">Full Name:</span>
//                     <span class="detail-value">${guest.firstname || ""} ${guest.lastname || ""}</span>
//                   </div>
//                   <div class="detail-row">
//                     <span class="detail-label">Phone Number:</span>
//                     <span class="detail-value">${guest.countrycode || ""} ${guest.phonenumber || "N/A"}</span>
//                   </div>
//                   <div class="detail-row">
//                     <span class="detail-label">Email ID:</span>
//                     <span class="detail-value">${guest.emailid || "N/A"}</span>
//                   </div>
//                   <div class="detail-row">
//                     <span class="detail-label">Country:</span>
//                     <span class="detail-value">${guest.country || "N/A"}</span>
//                   </div>
//                   <div class="detail-row">
//                     <span class="detail-label">Check-in Date:</span>
//                     <span class="detail-value">${guest.check_in_date || "N/A"}</span>
//                   </div>
//                   <div class="detail-row">
//                     <span class="detail-label">Check-out Date:</span>
//                     <span class="detail-value">${guest.check_out_date || "N/A"}</span>
//                   </div>
//                   <div class="detail-row">
//                     <span class="detail-label">Room Number:</span>
//                     <span class="detail-value">${guest.room_number ? "#" + guest.room_number : "N/A"}</span>
//                   </div>
//                   <div class="detail-row">
//                     <span class="detail-label">Room Type:</span>
//                     <span class="detail-value">${guest.room_type || "N/A"}</span>
//                   </div>
//                   <div class="detail-row">
//                     <span class="detail-label">Purpose of Visit:</span>
//                     <span class="detail-value">${guest.purpose_of_visit || "N/A"}</span>
//                   </div>
//                   <div class="detail-row">
//                     <span class="detail-label">Number of Guests:</span>
//                     <span class="detail-value">${guest.number_of_guests || "1"}</span>
//                   </div>
//                 </div>
//                 <div class="signature-section">
//                   <div class="signature-box">
//                     ${
//                       guest.signature_url
//                         ? `<img src="${guest.signature_url}" class="signature-image" alt="Guest Signature" onerror="this.parentElement.innerHTML='<div style=\\\\'color:#9ca3af;\\\\'>No Signature Available</div>'" />`
//                         : `<div style="color: #9ca3af;">No Signature Available</div>`
//                     }
//                   </div>
//                   <div class="signature-label">Guest Signature</div>
//                   ${
//                     guest.signature_saved_at
//                       ? `<div class="signature-label">Signed on: ${new Date(guest.signature_saved_at).toLocaleDateString()}</div>`
//                       : ""
//                   }
//                 </div>
//               </div>
//               <div class="aadhaar-section">
//                 <div class="aadhaar-info">
//                   <span class="aadhaar-badge">AADHAAR</span>
//                   ${
//                     guest.aadhaar_verified
//                       ? `<span class="status-verified verification-status">✓ Verified</span>`
//                       : `<span class="status-pending verification-status">⚠ Pending</span>`
//                   }
//                   ${
//                     guest.aadhaar_hash
//                       ? `<span class="aadhaar-number">ID: ${guest.aadhaar_hash.substring(0, 12)}...</span>`
//                       : ""
//                   }
//                 </div>
//                 <div>
//                   ${
//                     guest.verification_status === "completed"
//                       ? `<span class="status-verified verification-status">✓ Police Verification Completed</span>`
//                       : `<span class="status-pending verification-status">⚠ Police Verification Pending</span>`
//                   }
//                 </div>
//               </div>
//             </div>
//           `,
//             )
//             .join("")}

//           <!-- Signature & Stamp Section -->
//           <div class="signature-stamp">
//             <div class="signature-line">
//               <div class="line">Authorized Signatory</div>
//               <div style="font-size: 10px; margin-top: 5px;">Hotel Manager</div>
//             </div>
//             <div class="signature-line">
//               <div class="line">Police Station Stamp</div>
//               <div style="font-size: 10px; margin-top: 5px;">(For Official Use)</div>
//             </div>
//             <div class="signature-line">
//               <div class="line">Receiving Officer Signature</div>
//               <div style="font-size: 10px; margin-top: 5px;">Police Department</div>
//             </div>
//           </div>

//           <!-- Footer -->
//           <div class="footer">
//             <p>This is a computer-generated police verification report. All information is based on hotel records.</p>
//             <p>For verification or additional information, please contact: ${currentUser?.email || "hotel@example.com"} | Phone: +91-XXXXXXXXXX</p>
//             <p>© ${new Date().getFullYear()} Hotel Management System - Police Verification Report v1.0</p>
//           </div>
//         </div>

//         <script>
//           // Force images to load before printing
//           window.onload = function() {
//             var images = document.getElementsByTagName('img');
//             var imagesLoaded = 0;
//             var totalImages = images.length;

//             function imageLoaded() {
//               imagesLoaded++;
//               if (imagesLoaded === totalImages) {
//                 window.print();
//               }
//             }

//             for (var i = 0; i < totalImages; i++) {
//               if (images[i].complete) {
//                 imageLoaded();
//               } else {
//                 images[i].addEventListener('load', imageLoaded);
//                 images[i].addEventListener('error', imageLoaded);
//               }
//             }

//             // If no images, print immediately
//             if (totalImages === 0) {
//               window.print();
//             }
//           };
//         </script>
//       </body>
//     </html>
//   `);
//     printWindow.document.close();
//   };
//   // Handle Email with proper modal
//   const handleSendEmail = async () => {
//     if (!emailData.recipientEmail) {
//       alert("Please enter recipient email address");
//       return;
//     }

//     setSendingEmail(true);
//     try {
//       // Simulate email sending
//       await new Promise((resolve) => setTimeout(resolve, 1500));

//       console.log("Email sent to:", emailData.recipientEmail);
//       console.log("Subject:", emailData.subject);
//       console.log("Message:", emailData.message);

//       alert(`Report sent successfully to ${emailData.recipientEmail}!`);
//       setShowEmailModal(false);
//     } catch (err) {
//       console.error("Error sending email:", err);
//       alert("Failed to send email. Please try again.");
//     } finally {
//       setSendingEmail(false);
//     }
//   };

//   // Handle Export
//   const handleExport = async () => {
//     try {
//       const response = await axios.get(
//         `${BQ_BASE_URL}/bq/api/reports/police-verification/export`,
//         {
//           params: {
//             month: selectedMonth,
//             property_id: propertyId,
//             verification_status:
//               filterStatus !== "all" ? filterStatus : undefined,
//           },
//           withCredentials: true,
//           responseType: "blob",
//         },
//       );

//       const url = window.URL.createObjectURL(new Blob([response.data]));
//       const link = document.createElement("a");
//       link.href = url;
//       link.setAttribute("download", `police_verification_${selectedMonth}.csv`);
//       document.body.appendChild(link);
//       link.click();
//       link.remove();
//       window.URL.revokeObjectURL(url);

//       alert("Report exported successfully!");
//     } catch (err) {
//       console.error("Error exporting report:", err);
//       alert("Failed to export report. Please try again.");
//     }
//   };

//   // Filter guests based on search and status
//   const getFilteredGuests = () => {
//     if (!reportData?.guests) return [];

//     let filtered = [...reportData.guests];

//     if (searchTerm) {
//       filtered = filtered.filter(
//         (guest) =>
//           guest.firstname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
//           guest.lastname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
//           guest.booking_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
//           guest.order_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
//           guest.emailid?.toLowerCase().includes(searchTerm.toLowerCase()) ||
//           guest.phonenumber?.toString().includes(searchTerm),
//       );
//     }

//     if (filterStatus !== "all") {
//       filtered = filtered.filter(
//         (guest) => guest.verification_status === filterStatus,
//       );
//     }

//     return filtered;
//   };

//   const filteredGuests = getFilteredGuests();

//   const getStatusBadge = (status) => {
//     switch (status) {
//       case "completed":
//         return (
//           <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 flex items-center gap-1">
//             <FaCheckCircle className="w-3 h-3" /> Verified
//           </span>
//         );
//       case "pending":
//         return (
//           <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800 flex items-center gap-1">
//             <FaExclamationTriangle className="w-3 h-3" /> Pending
//           </span>
//         );
//       default:
//         return (
//           <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
//             Unknown
//           </span>
//         );
//     }
//   };

//   const getAadhaarStatusBadge = (isVerified, country) => {
//     const isIndian =
//       country?.toLowerCase() === "india" || country?.toLowerCase() === "indian";

//     if (!isIndian) {
//       return (
//         <span className="text-xs text-gray-500 flex items-center gap-1">
//           <FaPassport className="w-3 h-3" /> Not Applicable
//         </span>
//       );
//     }
//     if (isVerified) {
//       return (
//         <span className="text-xs text-green-600 flex items-center gap-1">
//           <FaCheckCircle className="w-3 h-3" /> Aadhaar Verified
//         </span>
//       );
//     }
//     return (
//       <span className="text-xs text-red-600 flex items-center gap-1">
//         <FaExclamationTriangle className="w-3 h-3" /> Aadhaar Pending
//       </span>
//     );
//   };

//   const summary = reportData?.summary || {
//     total_guests: 0,
//     monthly_arrivals: 0,
//     pending_verification: 0,
//     verified_guests: 0,
//     aadhaar_verified: 0,
//     total_indian_guests: 0,
//     total_foreign_guests: 0,
//     occupancy_rate: 0,
//     avg_stay_days: 0,
//     unique_rooms_occupied: 0,
//   };

//   if (loading && !reportData) {
//     return (
//       <div className="min-h-screen bg-gray-50 flex items-center justify-center">
//         <div className="text-center">
//           <FaSpinner className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
//           <p className="text-gray-600">Loading police verification report...</p>
//         </div>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="min-h-screen bg-gray-50 flex items-center justify-center">
//         <div className="text-center">
//           <FaExclamationTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
//           <p className="text-red-600 mb-2">Error loading report</p>
//           <p className="text-gray-600">{error}</p>
//           <button
//             onClick={() => {
//               setError(null);
//               setLoading(true);
//               fetchReportData();
//             }}
//             className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
//           >
//             Retry
//           </button>
//         </div>
//       </div>
//     );
//   }

//   // Guest Details Modal Component
//   const GuestDetailsModal = ({ guest, onClose }) => {
//     const [showFullImage, setShowFullImage] = useState(false);
//     const [selectedImageUrl, setSelectedImageUrl] = useState("");

//     if (!guest) return null;

//     const openImageViewer = (imageUrl) => {
//       setSelectedImageUrl(imageUrl);
//       setShowFullImage(true);
//     };

//     const formatDate = (dateString) => {
//       if (!dateString) return "N/A";
//       return new Date(dateString).toLocaleDateString("en-US", {
//         year: "numeric",
//         month: "short",
//         day: "numeric",
//       });
//     };

//     const formatPhoneNumber = (countryCode, phoneNumber) => {
//       if (!phoneNumber) return "N/A";
//       return `${countryCode || ""} ${phoneNumber}`;
//     };

//     return (
//       <>
//         <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
//           <div className="bg-white rounded-xl border border-gray-200 w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
//             {/* Header */}
//             <div className="bg-white p-6 border-b border-gray-200">
//               <div className="flex items-center justify-between">
//                 <div className="flex items-center space-x-4">
//                   <div className="relative">
//                     {guest.profile_photo ? (
//                       <img
//                         src={guest.profile_photo}
//                         alt="Profile"
//                         className="w-16 h-16 rounded-full border-2 border-gray-300 object-cover cursor-pointer"
//                         onClick={() => openImageViewer(guest.profile_photo)}
//                       />
//                     ) : (
//                       <div className="w-16 h-16 rounded-full bg-gray-100 border-2 border-gray-300 flex items-center justify-center">
//                         <FaUsers className="text-xl text-gray-400" />
//                       </div>
//                     )}
//                   </div>
//                   <div>
//                     <h2 className="text-xl font-bold text-gray-900">
//                       {guest.firstname} {guest.lastname}
//                     </h2>
//                     <p className="text-gray-600 text-sm">
//                       Reservation ID: {guest.booking_id} • Order ID:{" "}
//                       {guest.order_id}
//                     </p>
//                     <div className="flex items-center space-x-2 mt-1">
//                       <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded border border-gray-300">
//                         Guest ID: {guest.guest_id?.substring(0, 8)}...
//                       </span>
//                       {guest.verification_status === "completed" && (
//                         <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded border border-green-300 flex items-center space-x-1">
//                           <FaCheckCircle className="text-xs" />
//                           <span>Verified</span>
//                         </span>
//                       )}
//                     </div>
//                   </div>
//                 </div>
//                 <button
//                   onClick={onClose}
//                   className="text-gray-400 hover:text-gray-600 text-xl font-bold p-2 rounded-full hover:bg-gray-100 transition"
//                 >
//                   ✕
//                 </button>
//               </div>
//             </div>

//             {/* Content */}
//             <div className="flex-1 overflow-y-auto p-6">
//               <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
//                 {/* Left Column */}
//                 <div className="xl:col-span-2 space-y-6">
//                   {/* Personal Information */}
//                   <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
//                     <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
//                       <FaUserCheck className="text-gray-600" />
//                       <span>Personal Information</span>
//                     </h3>
//                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                       <div className="space-y-3">
//                         <div className="flex justify-between items-center py-2 border-b border-gray-200">
//                           <span className="text-sm font-medium text-gray-600">
//                             Full Name
//                           </span>
//                           <span className="text-sm font-semibold text-gray-900">
//                             {guest.firstname} {guest.lastname}
//                           </span>
//                         </div>
//                         <div className="flex justify-between items-center py-2 border-b border-gray-200">
//                           <span className="text-sm font-medium text-gray-600">
//                             Email
//                           </span>
//                           <span className="text-sm text-gray-900 flex items-center space-x-1">
//                             <FaMailIcon className="text-gray-400" />
//                             <span>{guest.emailid || "N/A"}</span>
//                           </span>
//                         </div>
//                         <div className="flex justify-between items-center py-2 border-b border-gray-200">
//                           <span className="text-sm font-medium text-gray-600">
//                             Country
//                           </span>
//                           <span className="text-sm text-gray-900">
//                             {guest.country}
//                           </span>
//                         </div>
//                       </div>
//                       <div className="space-y-3">
//                         <div className="flex justify-between items-center py-2 border-b border-gray-200">
//                           <span className="text-sm font-medium text-gray-600">
//                             Phone
//                           </span>
//                           <span className="text-sm font-semibold text-gray-900 flex items-center space-x-1">
//                             <FaPhone className="text-gray-400" />
//                             <span>
//                               {formatPhoneNumber(
//                                 guest.countrycode,
//                                 guest.phonenumber,
//                               )}
//                             </span>
//                           </span>
//                         </div>
//                         <div className="flex justify-between items-center py-2 border-b border-gray-200">
//                           <span className="text-sm font-medium text-gray-600">
//                             Purpose of Visit
//                           </span>
//                           <span className="text-sm text-gray-900">
//                             {guest.purpose_of_visit || "N/A"}
//                           </span>
//                         </div>
//                         <div className="flex justify-between items-center py-2">
//                           <span className="text-sm font-medium text-gray-600">
//                             Number of Guests
//                           </span>
//                           <span className="text-sm font-semibold text-gray-900">
//                             {guest.number_of_guests || "1"}
//                           </span>
//                         </div>
//                       </div>
//                     </div>
//                   </div>

//                   {/* Stay Details */}
//                   <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
//                     <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
//                       <FaHotel className="text-gray-600" />
//                       <span>Stay Details</span>
//                     </h3>
//                     <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
//                       <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
//                         <BsCalendarCheck className="w-5 h-5 text-blue-500 mx-auto mb-2" />
//                         <p className="text-xs text-gray-500">Check-in</p>
//                         <p className="text-sm font-semibold">
//                           {guest.check_in_date || "N/A"}
//                         </p>
//                       </div>
//                       <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
//                         <BsCalendarCheck className="w-5 h-5 text-red-500 mx-auto mb-2" />
//                         <p className="text-xs text-gray-500">Check-out</p>
//                         <p className="text-sm font-semibold">
//                           {guest.check_out_date || "N/A"}
//                         </p>
//                       </div>
//                       <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
//                         <FaBed className="w-5 h-5 text-purple-500 mx-auto mb-2" />
//                         <p className="text-xs text-gray-500">Room Number</p>
//                         <p className="text-sm font-semibold">
//                           #{guest.room_number || "N/A"}
//                         </p>
//                       </div>
//                       <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
//                         <FaBuilding className="w-5 h-5 text-green-500 mx-auto mb-2" />
//                         <p className="text-xs text-gray-500">Room Type</p>
//                         <p className="text-sm font-semibold">
//                           {guest.room_type || "N/A"}
//                         </p>
//                       </div>
//                     </div>
//                   </div>

//                   {/* Aadhaar Card Section */}
//                   <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
//                     <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
//                       <FaIdCard className="text-gray-600" />
//                       <span>Aadhaar Card</span>
//                     </h3>
//                     {guest.aadhar_image_url ? (
//                       <div className="text-center">
//                         <img
//                           src={guest.aadhar_image_url}
//                           alt="Aadhaar Card"
//                           className="max-w-full h-auto max-h-64 rounded border border-gray-300 mx-auto cursor-pointer hover:shadow-lg transition"
//                           onClick={() =>
//                             openImageViewer(guest.aadhar_image_url)
//                           }
//                         />
//                         <p className="text-gray-600 text-sm mt-3 flex items-center justify-center space-x-2">
//                           <FaCheckCircle className="text-green-600" />
//                           <span>Aadhaar document available</span>
//                         </p>
//                       </div>
//                     ) : (
//                       <div className="text-center py-8">
//                         <div className="w-32 h-32 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg mx-auto flex items-center justify-center">
//                           <FaIdCard className="text-4xl text-gray-400" />
//                         </div>
//                         <p className="text-gray-600 text-sm mt-3">
//                           No Aadhaar card uploaded
//                         </p>
//                       </div>
//                     )}
//                   </div>
//                 </div>

//                 {/* Right Column */}
//                 <div className="space-y-6">
//                   {/* Verification Status */}
//                   <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
//                     <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
//                       <MdOutlineVerifiedUser className="text-gray-600" />
//                       <span>Verification Status</span>
//                     </h3>
//                     <div className="space-y-3">
//                       <div
//                         className={`flex items-center justify-between p-3 rounded-lg border ${guest.verification_status === "completed" ? "bg-white border-green-300" : "bg-white border-gray-300"}`}
//                       >
//                         <div className="flex items-center space-x-3">
//                           <div
//                             className={`p-2 rounded-full ${guest.verification_status === "completed" ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-400"}`}
//                           >
//                             {guest.verification_status === "completed" ? (
//                               <FaCheckCircle />
//                             ) : (
//                               <FaTimesCircle />
//                             )}
//                           </div>
//                           <div>
//                             <p className="font-semibold text-gray-900">
//                               Police Verification
//                             </p>
//                             <p className="text-xs text-gray-600">
//                               {guest.verification_status === "completed"
//                                 ? "Verification completed"
//                                 : "Verification pending"}
//                             </p>
//                           </div>
//                         </div>
//                         <span
//                           className={`px-2 py-1 rounded-full text-xs font-semibold ${guest.verification_status === "completed" ? "bg-green-100 text-green-800 border border-green-300" : "bg-yellow-100 text-yellow-800 border border-yellow-300"}`}
//                         >
//                           {guest.verification_status === "completed"
//                             ? "VERIFIED"
//                             : "PENDING"}
//                         </span>
//                       </div>

//                       <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-300">
//                         <div className="flex items-center space-x-3">
//                           <div
//                             className={`p-2 rounded-full ${guest.aadhaar_verified ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-400"}`}
//                           >
//                             <FaIdCard />
//                           </div>
//                           <div>
//                             <p className="font-semibold text-gray-900">
//                               Aadhaar Verification
//                             </p>
//                             <p className="text-xs text-gray-600">
//                               {guest.aadhaar_verified
//                                 ? "Verified"
//                                 : "Not verified"}
//                             </p>
//                           </div>
//                         </div>
//                         <FaCheckCircle
//                           className={
//                             guest.aadhaar_verified
//                               ? "text-green-600"
//                               : "text-gray-300"
//                           }
//                         />
//                       </div>

//                       <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-300">
//                         <div className="flex items-center space-x-3">
//                           <div
//                             className={`p-2 rounded-full ${guest.has_face_embedding ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-400"}`}
//                           >
//                             <BsFillPersonCheckFill />
//                           </div>
//                           <div>
//                             <p className="font-semibold text-gray-900">
//                               Face Recognition
//                             </p>
//                             <p className="text-xs text-gray-600">
//                               {guest.has_face_embedding
//                                 ? "Face registered"
//                                 : "No face data"}
//                             </p>
//                           </div>
//                         </div>
//                         <FaCheckCircle
//                           className={
//                             guest.has_face_embedding
//                               ? "text-green-600"
//                               : "text-gray-300"
//                           }
//                         />
//                       </div>
//                     </div>
//                   </div>

//                   {/* Signature Section */}
//                   <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
//                     <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
//                       <FaFileAlt className="text-gray-600" />
//                       <span>Guest Signature</span>
//                     </h3>
//                     {guest.signature_url ? (
//                       <div className="text-center">
//                         <div className="bg-white p-4 rounded-lg border border-gray-300">
//                           <img
//                             src={guest.signature_url}
//                             alt="Guest Signature"
//                             className="max-w-full h-auto max-h-32 mx-auto cursor-pointer"
//                             onClick={() => openImageViewer(guest.signature_url)}
//                           />
//                         </div>
//                         <p className="text-xs text-gray-500 mt-3 flex items-center justify-center space-x-1">
//                           <FaCheckCircle className="text-green-600 w-3 h-3" />
//                           <span>
//                             Signature captured on{" "}
//                             {guest.signature_saved_at
//                               ? formatDate(guest.signature_saved_at)
//                               : "check-in"}
//                           </span>
//                         </p>
//                       </div>
//                     ) : (
//                       <div className="text-center py-6">
//                         <div className="w-full h-32 bg-white border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
//                           <FaFileAlt className="text-4xl text-gray-400" />
//                         </div>
//                         <p className="text-gray-600 text-sm mt-3">
//                           No signature available
//                         </p>
//                       </div>
//                     )}
//                   </div>

//                   {/* Face Match Status */}
//                   <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
//                     <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
//                       <BsFillPersonCheckFill className="text-gray-600" />
//                       <span>Face Recognition Status</span>
//                     </h3>
//                     <div className="text-center">
//                       <div
//                         className={`p-4 rounded-lg ${guest.face_matched ? "bg-green-100 border border-green-300" : "bg-yellow-100 border border-yellow-300"}`}
//                       >
//                         {guest.face_matched ? (
//                           <>
//                             <FaCheckCircle className="w-12 h-12 text-green-600 mx-auto mb-2" />
//                             <p className="font-semibold text-green-800">
//                               Face Matched Successfully
//                             </p>
//                           </>
//                         ) : (
//                           <>
//                             <FaExclamationTriangle className="w-12 h-12 text-yellow-600 mx-auto mb-2" />
//                             <p className="font-semibold text-yellow-800">
//                               Face Not Matched
//                             </p>
//                           </>
//                         )}
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Footer */}
//             <div className="border-t border-gray-200 p-6 bg-gray-50">
//               <div className="flex justify-between items-center">
//                 <div className="text-sm text-gray-600">
//                   Report generated for police verification
//                 </div>
//                 <div className="flex space-x-3">
//                   <button
//                     onClick={handlePrint}
//                     className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition flex items-center space-x-2"
//                   >
//                     <FaPrint className="text-sm" />
//                     <span>Print Details</span>
//                   </button>
//                   <button
//                     onClick={onClose}
//                     className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition font-semibold"
//                   >
//                     Close
//                   </button>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Full Screen Image Viewer */}
//         {showFullImage && (
//           <div
//             className="fixed inset-0 bg-black bg-opacity-90 z-[60] flex items-center justify-center p-4"
//             onClick={() => setShowFullImage(false)}
//           >
//             <div className="relative max-w-5xl max-h-[90vh]">
//               <button
//                 onClick={() => setShowFullImage(false)}
//                 className="absolute -top-12 right-0 text-white hover:text-gray-300 text-2xl"
//               >
//                 ✕
//               </button>
//               <img
//                 src={selectedImageUrl}
//                 alt="Full View"
//                 className="max-w-full max-h-[85vh] object-contain rounded-lg"
//               />
//             </div>
//           </div>
//         )}
//       </>
//     );
//   };

//   // Email Modal Component
//   const EmailModal = () => (
//     <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
//       <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
//         <div className="p-6 border-b border-gray-200 flex justify-between items-center">
//           <h2 className="text-2xl font-bold flex items-center gap-2">
//             <FaEnvelope className="text-blue-600" /> Send Report via Email
//           </h2>
//           <button
//             onClick={() => setShowEmailModal(false)}
//             className="text-gray-400 hover:text-gray-600"
//           >
//             <FaTimes className="text-xl" />
//           </button>
//         </div>

//         <div className="p-6 overflow-y-auto">
//           <div className="space-y-4">
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Recipient Email <span className="text-red-500">*</span>
//               </label>
//               <input
//                 type="email"
//                 value={emailData.recipientEmail}
//                 onChange={(e) =>
//                   setEmailData({ ...emailData, recipientEmail: e.target.value })
//                 }
//                 placeholder="police@example.com"
//                 className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
//               />
//               <p className="text-xs text-gray-500 mt-1">
//                 Enter the email address of the police department
//               </p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Subject
//               </label>
//               <input
//                 type="text"
//                 value={emailData.subject}
//                 onChange={(e) =>
//                   setEmailData({ ...emailData, subject: e.target.value })
//                 }
//                 className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Message
//               </label>
//               <textarea
//                 rows={6}
//                 value={emailData.message}
//                 onChange={(e) =>
//                   setEmailData({ ...emailData, message: e.target.value })
//                 }
//                 className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
//               />
//             </div>

//             <div className="flex items-center gap-3">
//               <input
//                 type="checkbox"
//                 id="includeAttachment"
//                 checked={emailData.includeAttachment}
//                 onChange={(e) =>
//                   setEmailData({
//                     ...emailData,
//                     includeAttachment: e.target.checked,
//                   })
//                 }
//                 className="w-4 h-4 text-blue-600 rounded"
//               />
//               <label
//                 htmlFor="includeAttachment"
//                 className="text-sm text-gray-700"
//               >
//                 Include CSV report as attachment
//               </label>
//             </div>
//           </div>
//         </div>

//         <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
//           <button
//             onClick={() => setShowEmailModal(false)}
//             className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
//           >
//             Cancel
//           </button>
//           <button
//             onClick={handleSendEmail}
//             disabled={sendingEmail || !emailData.recipientEmail}
//             className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-2"
//           >
//             {sendingEmail ? (
//               <FaSpinner className="animate-spin" />
//             ) : (
//               <FaEnvelope />
//             )}
//             {sendingEmail ? "Sending..." : "Send Email"}
//           </button>
//         </div>
//       </div>
//     </div>
//   );

//   return (
//     <div className="min-h-screen bg-gray-50 p-6">
//       {/* Email Modal */}
//       {showEmailModal && <EmailModal />}

//       {/* Header */}
//       <div className="mb-6">
//         <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
//           <MdOutlineVerifiedUser className="text-blue-600" /> Police
//           Verification Report
//         </h1>
//         <p className="text-gray-800 font-semibold mt-1">
//           Comprehensive guest verification records for regulatory compliance
//         </p>
//       </div>

//       {/* Summary Cards */}
//       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-2">
//         <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500 hover:shadow-lg transition">
//           <div className="flex items-center justify-between">
//             <div>
//               <p className="text-sm text-gray-500">Total Guests (Month)</p>
//               <p className="text-2xl font-bold">{summary.total_guests}</p>
//             </div>
//             <FaUsers className="w-10 h-10 text-blue-500" />
//           </div>
//         </div>
//         <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500 hover:shadow-lg transition">
//           <div className="flex items-center justify-between">
//             <div>
//               <p className="text-sm text-gray-500">Monthly Arrivals</p>
//               <p className="text-2xl font-bold">{summary.monthly_arrivals}</p>
//               <p className="text-xs text-gray-500 mt-1">{selectedMonth}</p>
//             </div>
//             <FaChartLine className="w-10 h-10 text-green-500" />
//           </div>
//         </div>
//         <div className="bg-white rounded-lg shadow p-4 border-l-4 border-yellow-500 hover:shadow-lg transition">
//           <div className="flex items-center justify-between">
//             <div>
//               <p className="text-sm text-gray-500">Pending Verification</p>
//               <p className="text-2xl font-bold text-yellow-600">
//                 {summary.pending_verification}
//               </p>
//               {summary.pending_verification > 0 && (
//                 <p className="text-xs text-red-600 mt-1">Action Required!</p>
//               )}
//             </div>
//             <MdPending className="w-10 h-10 text-yellow-500" />
//           </div>
//         </div>
//         <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500 hover:shadow-lg transition">
//           <div className="flex items-center justify-between">
//             <div>
//               <p className="text-sm text-gray-500">Foreign Guests</p>
//               <p className="text-2xl font-bold">
//                 {summary.total_foreign_guests}
//               </p>
//             </div>
//             <FaBuilding className="w-10 h-10 text-purple-500" />
//           </div>
//         </div>
//       </div>

//       {/* Monthly Stats Summary */}
//       {statsData?.monthly_stats &&
//         statsData.monthly_stats.some((stat) => stat.total_guests > 0) && (
//           <div className="px-1 py-3 bg-gray-50">
//             <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
//               <FaChartLine /> Monthly Trend (Last 6 Months)
//             </h3>
//             <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
//               {statsData.monthly_stats.map((stat, idx) => (
//                 <div
//                   key={idx}
//                   className="bg-white rounded-lg p-3 shadow hover:shadow-lg transition"
//                 >
//                   <div className="text-center mb-2">
//                     <span className="font-semibold text-sm">{stat.month}</span>
//                   </div>
//                   <div className="text-center">
//                     <div className="text-2xl font-bold text-blue-600">
//                       {stat.total_guests}
//                     </div>
//                     <div className="text-xs text-gray-500">Guests</div>
//                   </div>
//                   <div className="mt-2 space-y-1 text-xs">
//                     <div className="flex justify-between">
//                       <span>Verified:</span>
//                       <span className="text-green-600">
//                         {stat.verified_guests}
//                       </span>
//                     </div>
//                     <div className="flex justify-between">
//                       <span>Pending:</span>
//                       <span className="text-yellow-600">
//                         {stat.pending_guests}
//                       </span>
//                     </div>
//                   </div>
//                 </div>
//               ))}
//             </div>
//           </div>
//         )}

//       {/* Second Row Summary Cards */}
//       <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
//         <div className="bg-white rounded-lg shadow p-3 hover:shadow-lg transition">
//           <div className="flex justify-between items-center">
//             <span className="text-sm text-gray-600 flex items-center gap-1">
//               <FaIdCard /> Aadhaar Verified
//             </span>
//             <span className="text-lg font-bold text-green-600">
//               {summary.aadhaar_verified}
//             </span>
//           </div>
//           <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
//             <div
//               className="bg-green-500 rounded-full h-2"
//               style={{
//                 width: `${summary.total_indian_guests > 0 ? (summary.aadhaar_verified / summary.total_indian_guests) * 100 : 0}%`,
//               }}
//             ></div>
//           </div>
//           <p className="text-xs text-gray-500 mt-1">
//             Indian Guests: {summary.total_indian_guests}
//           </p>
//         </div>
//         <div className="bg-white rounded-lg shadow p-3 hover:shadow-lg transition">
//           <div className="flex justify-between items-center">
//             <span className="text-sm text-gray-600 flex items-center gap-1">
//               <FaClock /> Avg. Stay Duration
//             </span>
//             <span className="text-lg font-bold text-blue-600">
//               {summary.avg_stay_days} days
//             </span>
//           </div>
//         </div>
//         <div className="bg-white rounded-lg shadow p-3 hover:shadow-lg transition">
//           <div className="flex justify-between items-center">
//             <span className="text-sm text-gray-600 flex items-center gap-1">
//               <FaHotel /> Current Occupancy
//             </span>
//             <span className="text-lg font-bold text-purple-600">
//               {summary.occupancy_rate}%
//             </span>
//           </div>
//           <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
//             <div
//               className="bg-purple-500 rounded-full h-2"
//               style={{ width: `${summary.occupancy_rate}%` }}
//             ></div>
//           </div>
//           <p className="text-xs text-gray-500 mt-1">
//             Unique Rooms: {summary.unique_rooms_occupied}
//           </p>
//         </div>
//       </div>

//       {/* Action Buttons */}
//       <div className="bg-white rounded-lg shadow mb-6 p-4">
//         <div className="flex flex-wrap justify-between items-center gap-4">
//           <div className="flex gap-3">
//             <button
//               onClick={handlePrint}
//               className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
//             >
//               <FaPrint /> Print Report
//             </button>
//             <button
//               onClick={() => setShowEmailModal(true)}
//               className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
//             >
//               <FaEnvelope /> Email Report
//             </button>
//             <button
//               onClick={handleExport}
//               className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
//             >
//               <FaFileDownload /> Export CSV
//             </button>
//           </div>
//           <div className="flex gap-3">
//             <div className="relative">
//               <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
//               <input
//                 type="month"
//                 value={selectedMonth}
//                 onChange={(e) => setSelectedMonth(e.target.value)}
//                 className="pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//               />
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Filters */}
//       <div className="bg-white rounded-lg shadow mb-6 p-4">
//         <div className="flex flex-wrap gap-4">
//           <div className="flex-1 relative">
//             <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
//             <input
//               type="text"
//               placeholder="Search by name, booking ID, phone, or email..."
//               value={searchTerm}
//               onChange={(e) => setSearchTerm(e.target.value)}
//               className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//             />
//           </div>
//           <select
//             value={filterStatus}
//             onChange={(e) => setFilterStatus(e.target.value)}
//             className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//           >
//             <option value="all">All Status</option>
//             <option value="completed">Verified</option>
//             <option value="pending">Pending</option>
//           </select>
//         </div>
//       </div>

//       {/* Guests Table */}
//       <div className="bg-white rounded-lg shadow overflow-hidden">
//         <div className="overflow-x-auto">
//           <table
//             className="min-w-full divide-y divide-gray-200"
//             id="printable-table"
//           >
//             <thead className="bg-gray-50">
//               <tr>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   Guest Details
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   Stay Period
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   Room
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   Verification
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   Aadhaar Status
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   Actions
//                 </th>
//               </tr>
//             </thead>
//             <tbody className="bg-white divide-y divide-gray-200">
//               {filteredGuests.length === 0 ? (
//                 <tr>
//                   <td
//                     colSpan="6"
//                     className="px-6 py-8 text-center text-gray-500"
//                   >
//                     No guests found for the selected period
//                   </td>
//                 </tr>
//               ) : (
//                 filteredGuests.map((guest, index) => (
//                   <tr
//                     key={guest.booking_id || index}
//                     className="hover:bg-gray-50 transition"
//                   >
//                     <td className="px-6 py-4">
//                       <div className="flex items-center">
//                         <div className="flex-shrink-0 h-10 w-10">
//                           {guest.profile_photo ? (
//                             <img
//                               className="h-10 w-10 rounded-full object-cover"
//                               src={guest.profile_photo}
//                               alt=""
//                             />
//                           ) : (
//                             <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
//                               <FaUsers className="w-5 h-5 text-gray-400" />
//                             </div>
//                           )}
//                         </div>
//                         <div className="ml-4">
//                           <div className="text-sm font-medium text-gray-900">
//                             {guest.firstname} {guest.lastname}
//                           </div>
//                           <div className="text-sm text-gray-500 flex items-center gap-1">
//                             <FaPhone className="w-3 h-3" />{" "}
//                             {guest.countrycode || ""}
//                             {guest.phonenumber || "N/A"}
//                           </div>
//                           <div className="text-xs text-gray-400 flex items-center gap-1">
//                             <FaMailIcon className="w-3 h-3" />{" "}
//                             {guest.emailid || "N/A"}
//                           </div>
//                         </div>
//                       </div>
//                     </td>
//                     <td className="px-6 py-4">
//                       <div className="text-sm text-gray-900">
//                         <div className="flex items-center gap-1">
//                           <BsCalendarCheck className="w-3 h-3" /> In:{" "}
//                           {guest.check_in_date || "N/A"}
//                         </div>
//                         <div className="flex items-center gap-1 mt-1">
//                           <BsCalendarCheck className="w-3 h-3" /> Out:{" "}
//                           {guest.check_out_date || "N/A"}
//                         </div>
//                       </div>
//                     </td>
//                     <td className="px-6 py-4">
//                       <div className="text-sm text-gray-900 font-semibold">
//                         Room {guest.room_number || "N/A"}
//                       </div>
//                       <div className="text-xs text-gray-500">
//                         {guest.room_type || "N/A"}
//                       </div>
//                     </td>
//                     <td className="px-6 py-4">
//                       {getStatusBadge(guest.verification_status)}
//                       {guest.verification_date && (
//                         <div className="text-xs text-gray-500 mt-1">
//                           {new Date(
//                             guest.verification_date,
//                           ).toLocaleDateString()}
//                         </div>
//                       )}
//                     </td>
//                     <td className="px-6 py-4">
//                       {getAadhaarStatusBadge(
//                         guest.aadhaar_verified,
//                         guest.country,
//                       )}
//                     </td>
//                     <td className="px-6 py-4">
//                       <button
//                         onClick={() => {
//                           setSelectedGuest(guest);
//                           setShowDetailsModal(true);
//                         }}
//                         className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
//                       >
//                         <FaEye /> View Details
//                       </button>
//                     </td>
//                   </tr>
//                 ))
//               )}
//             </tbody>
//           </table>
//         </div>
//       </div>

//       {/* Pagination */}
//       <div className="flex justify-between items-center mt-6">
//         <div className="text-sm text-gray-500">
//           Showing {filteredGuests.length} of {reportData?.total_count || 0}{" "}
//           guests
//         </div>
//         <div className="flex gap-2">
//           <button
//             onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
//             disabled={currentPage === 1}
//             className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
//           >
//             Previous
//           </button>
//           <button className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700">
//             {currentPage}
//           </button>
//           <button
//             onClick={() => setCurrentPage((prev) => prev + 1)}
//             disabled={filteredGuests.length < itemsPerPage}
//             className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
//           >
//             Next
//           </button>
//         </div>
//       </div>

//       {/* Modals */}
//       {showDetailsModal && (
//         <GuestDetailsModal
//           guest={selectedGuest}
//           onClose={() => setShowDetailsModal(false)}
//         />
//       )}
//     </div>
//   );
// };

// export default PoliceVerificationReport;
